import * as React from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useStore } from 'react-redux';
import { cloneEntity, Entity, QueryOptions, Row } from 'gossamer-unified';
import { copyDataToClipboard } from './copyData';
import { getPageNumber, initializeQueryOptions, moveToPage } from './Utils';
import { useCacheQuery } from './useCacheQuery';
import { Paginator } from '../sections/Paginator';
import { ListSettings } from '../sections/ListSettings';
import styles from './QueryMulti.module.css';
import { useQueryOptionsChanges, useQueryOptionsFromSearchParams } from './useQueryOptions';
import { useCloneEntity } from '../ancillary/useCloneEntity';

export interface SubSectionMultiProps<T extends Row> {
  entity: Entity<T>;
  excludeValues?: string[];
  onRowClick?: (keyValue: string) => void;
  queryOptions?: QueryOptions;
  rowClickSetsSearchPaths?: boolean;
  rows: T[];
  updateQueryOptions?: (newQueryOptions: QueryOptions) => void;
}

interface URLParamsProps<T extends Row> {
  alterEntity?: (cloneEntity: Entity<T>) => void;
  entity: Entity<T>;
  excludeValues?: string[];
  onRowClick?: (keyValue: string) => void;
  render: React.FC<SubSectionMultiProps<T>>;
  showItemCount?: boolean;
  showSettingsControl?: boolean;
  showPaginator?: 'never' | 'as-required' | 'always';
}

type Props<T extends Row> = URLParamsProps<T> & {
  queryOptions?: QueryOptions;
};

export const QueryMulti = <T extends Row>(props: Props<T>): React.JSX.Element => {
  const LocalEntity = useCloneEntity(props.entity, props.alterEntity);
  const [click, setClick] = React.useState(0);
  const [queryOptions, setQueryOptions] = useQueryOptionsChanges(
    LocalEntity,
    initializeQueryOptions(LocalEntity, props.queryOptions || {}),
  );
  return (
    <QueryMultiInner
      click={() => setClick(click + 1)}
      localEntity={LocalEntity}
      queryOptions={queryOptions}
      setQueryOptions={setQueryOptions}
      excludeValues={props.excludeValues}
      onRowClick={props.onRowClick}
      render={props.render}
      showItemCount={props.showItemCount}
      showSettingsControl={props.showSettingsControl}
      showPaginator={props.showPaginator}
    />
  );
};

export const QueryMultiURLParams = <T extends Row>(props: URLParamsProps<T>): React.JSX.Element => {
  const LocalEntity = useCloneEntity(props.entity, props.alterEntity);
  const [click, setClick] = React.useState(0);
  const [queryOptions, setQueryOptions] = useQueryOptionsFromSearchParams(LocalEntity);
  return (
    <QueryMultiInner
      click={() => setClick(click + 1)}
      localEntity={LocalEntity}
      queryOptions={queryOptions}
      setQueryOptions={setQueryOptions}
      excludeValues={props.excludeValues}
      onRowClick={props.onRowClick}
      render={props.render}
      showItemCount={props.showItemCount}
      showSettingsControl={props.showSettingsControl}
      showPaginator={props.showPaginator}
    />
  );
};

interface InnerProps<T extends Row> {
  click: () => void;
  excludeValues?: string[];
  localEntity: Entity<T>;
  onRowClick?: (keyValue: string) => void;
  queryOptions: QueryOptions;
  setQueryOptions: (queryOptions: QueryOptions) => void;
  render: React.FC<SubSectionMultiProps<T>>;
  showItemCount?: boolean;
  showSettingsControl?: boolean;
  showPaginator?: 'never' | 'as-required' | 'always';
}

export const QueryMultiInner = <T extends Row>(props: InnerProps<T>): React.JSX.Element => {
  const store = useStore() as any;
  const { data, error, isFetching } = useCacheQuery(props.localEntity, props.queryOptions);
  const SubSection = props.render;
  const [pageNumber, pageSize] = getPageNumber(props.queryOptions);
  const showPaginator =
    props.showPaginator === 'always' ||
    ((!props.showPaginator || props.showPaginator === 'as-required') &&
      ((data?.length || 0) >= pageSize || pageNumber > 1));
  const mainClassName = isFetching ? styles.FetchingOpacity : '';
  return (
    <>
      <div className={mainClassName}>
        {data && !error && (
          <SubSection
            rows={data}
            excludeValues={props.excludeValues}
            entity={props.localEntity}
            onRowClick={props.onRowClick}
            queryOptions={props.queryOptions}
            rowClickSetsSearchPaths={true}
            updateQueryOptions={props.setQueryOptions}
          />
        )}
      </div>
      <div className={styles.QueryMultiFooter}>
        {showPaginator && !error && !isFetching && (
          <Paginator
            pageNumber={pageNumber}
            pageSize={pageSize}
            moveToPage={(newPage) => props.setQueryOptions(moveToPage(props.queryOptions, newPage))}
            rowsInPage={data?.length || 0}
          />
        )}
        {!showPaginator && !error && !isFetching && props.showItemCount !== false && data && (
          <div>{data.length === 0 ? 'none' : data.length === 1 ? '1 item' : data.length + ' items'}</div>
        )}
        {props.showSettingsControl !== false && !error && !isFetching && (
          <ListSettings
            click={props.click}
            entity={props.localEntity}
            handleCopyDataToClipboard={(includeHeader?: boolean) =>
              copyDataToClipboard(store, props.localEntity, data || [], includeHeader || false)
            }
            queryOptions={props.queryOptions}
            updateQueryOptions={props.setQueryOptions}
          />
        )}
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      {isFetching && (
        <div>
          <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
        </div>
      )}
    </>
  );
};

interface InitializeProps<T extends Row> {
  entity: Entity<T>;
  alterEntity?: (entity: Entity<T>) => void;
}

export const initializeEntity = <T extends Row>(props: InitializeProps<T>) => {
  const localEntity = cloneEntity(props.entity);
  if (props.alterEntity) {
    props.alterEntity(localEntity);
  }
  return localEntity;
};
